import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { List, ListRowRenderer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading, SectionHeading } from '@/components/Layout/Parts';
import PulsingLoader from '@/components/PulsingLoader';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { scrollBarStyles } from '@/utils/theme';

import { DQCommentsCheckerPost } from '../CommentsChecker';
import Blacklist from '../CommentsChecker/components/Blacklist';
import CommentRow, {
  Checkbox,
  CheckboxHolder,
  CheckboxLabel,
} from './components/CommentRow';
import PostInfoCard from './components/PostInfoCard';

const LoaderHolder = styled.div`
  text-align: center;
  padding: 60px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
  margin-bottom: 30px;
`;

const LoaderTextHolder = styled.div`
  margin: 0 auto;
`;

const LoaderTitle = styled(Text)`
  margin-top: 20px;
  margin-bottom: 6px;
`;

const ResultsCard = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
`;

const StyledList = styled(List)`
  ${scrollBarStyles};
`;

const Headings = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.grayscaleExtraLight};
  padding: 24px;
  gap: 24px;
  align-items: center;
`;

const ImageHeadingColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const UploadButtonHolder = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  right: 120px;
  z-index: 2;
`;

const UploadButton = styled(Button)`
  padding: 0 24px;
  box-shadow:
    0px 10px 30px rgba(34, 34, 34, 0.12),
    0px 20px 100px rgba(34, 34, 34, 0.06);
  min-width: 284px;
`;

const StyledCheckbox = styled(Checkbox)`
  &:checked + ${CheckboxLabel} {
    &::after {
      content: '';
      position: absolute;
      left: 8px;
      top: 3px;
      width: 8px;
      height: 14px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 0 0;
      transform: rotate(90deg);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 24px;

  ${PageHeading} {
    margin: 0;
  }
`;

const StyledSectionHeading = styled(SectionHeading)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const CommentsCheckerSingle = () => {
  const { commentsCheckId } = useParams();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [checkedComments, setCheckedComments] = useState<string[]>([]);

  const { data, isLoading } = useQuery(
    [
      'dq_post_comment_checks',
      {
        id: commentsCheckId,
      },
    ],
    () =>
      api.get<never, DQCommentsCheckerPost>(
        `/v1/editorial/comment-checks/${commentsCheckId}`,
      ),
    {
      retry: 2,
      refetchInterval: (data) => {
        if (data?.is_deleting) {
          return 10_000;
        }

        return false;
      },
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  const renderRow: ListRowRenderer = useCallback(
    ({ index, key, style, isVisible }) => {
      const comment = data!.malicious_comments![index];

      if (!isVisible) return null;

      return (
        <CommentRow
          key={key}
          style={style}
          comment={comment}
          onCheck={() => {
            setCheckedComments((prev) =>
              prev.includes(comment.id)
                ? prev.filter((p) => p !== comment.id)
                : [...prev, comment.id],
            );
          }}
          isChecked={checkedComments.includes(comment.id)}
        />
      );
    },
    [data, checkedComments],
  );

  const onToggleAll = () => {
    setCheckedComments((prev) => {
      if (!prev.length) {
        return data?.malicious_comments?.map((p) => p.id) ?? [];
      }

      return [];
    });
  };

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Delete ${checkedComments.length} comments`,
      );

      if (!confirmed) {
        return;
      }

      await api.post(
        `/v1/editorial/comment-checks/${commentsCheckId}/bulk-delete`,
        {
          comments: checkedComments,
        },
      );

      navigate('..', { relative: 'path' });

      await queryClient.invalidateQueries(['dq_post_comment_checks']);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const onApprove = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) return;

      await api.delete(`/v1/editorial/comment-checks/${commentsCheckId}`);

      alerts.close();

      navigate('../', { relative: 'path', replace: true });
    } catch (_) {
      alerts.error();
    }
  };

  if (isLoading || !data) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <Header>
        <PageHeading>Post: {data?.post.external_id}</PageHeading>
        <Button onClick={onApprove} disabled={data.is_deleting}>
          Approve
        </Button>
      </Header>
      <BackButton />
      <PostInfoCard data={data} />
      {data.is_deleting ? (
        <LoaderHolder>
          <PulsingLoader />
          <LoaderTextHolder>
            <LoaderTitle $type="button">Deleting comments ...</LoaderTitle>
            <Text $color="gray" $type="captionSemibold">
              Please be patient, this action may take
              <br />a little longer in some cases
            </Text>
          </LoaderTextHolder>
        </LoaderHolder>
      ) : (
        <>
          <AnimatePresence>
            {!!checkedComments.length && (
              <UploadButtonHolder
                initial={{ scale: 0, opacity: 0.3 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0.3 }}
              >
                <UploadButton onClick={onDelete} color="inverted">
                  Delete ({checkedComments.length}) comments
                </UploadButton>
              </UploadButtonHolder>
            )}
          </AnimatePresence>
          <StyledSectionHeading>
            <span>
              Malicious comments list ({data.malicious_comments!.length})
            </span>
            <Blacklist />
          </StyledSectionHeading>
          <ResultsCard>
            <Headings>
              <ImageHeadingColumn>
                <CheckboxHolder>
                  <StyledCheckbox
                    id={`comments-all`}
                    name="products"
                    type="checkbox"
                    checked={
                      checkedComments.length === data.malicious_comments?.length
                    }
                    onChange={onToggleAll}
                  />
                  <CheckboxLabel htmlFor={`comments-all`} />
                </CheckboxHolder>
              </ImageHeadingColumn>
              <Text $type="smallBold">Message</Text>
            </Headings>
            {!!data.malicious_comments!.length && (
              <StyledList
                width={1120}
                height={800}
                headerHeight={200}
                rowHeight={140}
                rowRenderer={renderRow}
                rowCount={data.malicious_comments!.length}
              />
            )}
          </ResultsCard>
        </>
      )}
    </>
  );
};

export default CommentsCheckerSingle;
